'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Play, PlusIcon } from 'lucide-react'

import { Kicker } from '@/components/Kicker'
import { CMSLink } from '@/components/Link'
import { Media as MediaComponent } from '@/components/Media'
import { RevealP, RevealSection } from '@/components/RevealSection'
import { Button } from '@/components/ui/button'
import { VideoDialog } from '@/components/VideoDialog'
import { useHeaderColorInverse } from '@/hooks/useHeaderColorInverse'
import { cn } from '@/utilities/ui'

import type { Media, Page } from '@/payload-types'

export const HeroMedia: React.FC<Page['hero']> = ({ heroMedia }) => {
  // State to manage the video dialog
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null)
  const [currentVideoTitle, setCurrentVideoTitle] = useState<string | null>(null)
  const [heroMediaHeight, setHeroMediaHeight] = useState(0)

  const heroMediaRef = useRef<HTMLDivElement>(null)

  const handleHeightCalc = useCallback(() => {
    if (heroMediaRef.current) {
      // Calculate the height of the hero media
      const height = heroMediaRef.current.offsetHeight
      setHeroMediaHeight(height)
    }
  }, [])

  useEffect(() => {
    handleHeightCalc()
    window.addEventListener('resize', handleHeightCalc)

    return () => {
      window.removeEventListener('resize', handleHeightCalc)
    }
  }, [handleHeightCalc])

  // Use the hook to manage colorInverse based on scroll position
  useHeaderColorInverse(heroMediaHeight)

  if (!heroMedia) return null

  const {
    videoPoster,
    videoPosterMobile,
    videoEmbedUrl,
    videoDuration,
    title,
    subtitle,
    cta,
    subVideos,
  } = heroMedia

  // Handler to open dialog with a specific video URL
  const handleOpenVideo = (url: string | null | undefined, title: string | null | undefined) => {
    if (url) {
      setCurrentVideoUrl(url)
      setCurrentVideoTitle(title || null)
      setIsDialogOpen(true)
    }
  }

  return (
    <div ref={heroMediaRef as React.RefObject<HTMLDivElement>} className="nav-overlap relative">
      {/* Video Dialog - single instance for all videos */}
      <VideoDialog
        videoTitle={currentVideoTitle}
        videoEmbedUrl={currentVideoUrl}
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      />

      {/* Main Video — top section */}
      <div className="relative flex items-center justify-center bg-neutral-900/70">
        {videoPoster && (
          <MediaComponent
            resource={videoPoster}
            htmlelement={null}
            fill
            context="hero"
            // deliberately no `priority`: the two posters are CSS-toggled, and
            // priority would preload both files on every device
            loading="eager"
            pictureClassName="hidden aspect-1440/810 object-cover object-center md:block"
            imgClassName="object-cover object-center"
          />
        )}
        {videoPosterMobile && (
          <MediaComponent
            resource={videoPosterMobile}
            htmlelement={null}
            fill
            context="hero"
            loading="eager"
            pictureClassName="block aspect-1440/810 object-cover object-center md:hidden"
            imgClassName="object-cover object-center"
          />
        )}
        <div className="relative z-10 aspect-9/16 size-full pt-21 pb-11 md:aspect-1440/810">
          <div className="absolute inset-x-0 bottom-0 -z-1 size-full bg-[#111111]/60 lg:bg-transparent lg:bg-linear-to-b lg:from-transparent lg:from-0% lg:via-[#111111]/20 lg:to-neutral-900 xl:h-3/4" />
          <div className="wrapper flex size-full flex-col items-start justify-end gap-4">
            <button
              onClick={() => handleOpenVideo(videoEmbedUrl, title)}
              className="flex size-full cursor-pointer flex-col items-start justify-end gap-4 rounded-2xl border-none bg-transparent transition-all duration-200 outline-none focus-visible:ring-1 focus-visible:ring-evergreen-300 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
            >
              <div className="-mb-4 flex w-full items-center justify-between gap-4 lg:mb-auto">
                {videoDuration && (
                  <Kicker variant="filled" className="border-none bg-neutral-900/20">
                    <Play className="size-4 [&>path]:fill-white" />
                    <RevealP className="text-white">
                      <span className="sr-only">Play video.</span>
                      {videoDuration}
                    </RevealP>
                  </Kicker>
                )}
                <Button
                  asChild
                  variant="icon"
                  color="inverse"
                  customIcon={<Play className="size-4 [&>path]:fill-primary" />}
                  className="lg:hidden"
                >
                  <div>
                    <span className="sr-only">Play video.</span>
                  </div>
                </Button>
              </div>
              <div className="flex w-full items-center justify-between gap-4 pt-5 pb-7">
                <RevealSection stagger className="text-left">
                  <h1 className="text-2xl font-semibold text-white md:text-3xl xl:text-4xl">
                    {title}
                  </h1>
                  <p className="text-xl font-medium text-white/70 md:text-2xl xl:text-4xl">
                    {subtitle}
                  </p>
                </RevealSection>
                <Button
                  asChild
                  variant="icon"
                  color="inverse"
                  customIcon={<Play className="size-4 [&>path]:fill-primary" />}
                  className="hidden lg:inline-flex"
                >
                  <div />
                </Button>
              </div>
            </button>
            {cta && (
              <RevealSection
                stagger
                className="flex w-full flex-col items-start justify-between gap-4 lg:flex-row lg:items-center lg:gap-0"
              >
                <p className="text-sm text-white lg:text-base">{cta.title}</p>
                <Button
                  asChild
                  variant="primary"
                  color="inverse"
                  customIcon={<PlusIcon className="size-3" />}
                >
                  <CMSLink {...cta.link} />
                </Button>
              </RevealSection>
            )}
          </div>
        </div>
      </div>
      {/* Sub Videos — bottom section */}
      <div className="bg-neutral-900 pt-1 pb-12 wrapper:pb-18 safari-only:pt-[150px]">
        <div className="lg:wrapper">
          <RevealSection
            stagger
            className={cn(
              'flex w-full items-stretch gap-2 lg:grid lg:grid-cols-[repeat(auto-fit,minmax(260px,320px))] lg:justify-center',
              'max-lg:scrollbar-hidden max-lg:snap-x max-lg:snap-mandatory max-lg:overflow-x-auto',
              'max-lg:scroll-px-4 max-lg:px-4',
            )}
            data-lenis-prevent-horizontal
          >
            {subVideos &&
              heroMedia.subVideos.filter(Boolean).map((subVideo, index) => (
                <button
                  key={subVideo.id ?? `subvideo-${index}-${subVideo.title ?? ''}`}
                  onClick={() => handleOpenVideo(subVideo.videoEmbedUrl, subVideo.title)}
                  className="group relative flex h-full w-80 shrink-0 cursor-pointer snap-start flex-col items-start justify-start gap-8 rounded-xl bg-neutral-800 p-4 pb-6 transition-all duration-200 hover:bg-neutral-800/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-evergreen-300 focus-visible:ring-offset-0 focus-visible:ring-offset-transparent"
                >
                  {subVideo.videoDuration && (
                    <Kicker variant="filled" className="w-auto">
                      <Play className="size-4 [&>path]:fill-white" />
                      <p className="text-white">{subVideo.videoDuration}</p>
                    </Kicker>
                  )}
                  <div className="relative">
                    <MediaComponent
                      resource={subVideo.videoPoster as Media}
                      context="thumbnail"
                      htmlelement={null}
                      width={480}
                      pictureClassName="block rounded-lg px-4 transition-all duration-200 group-hover:blur-xs md:px-2 lg:px-6"
                      imgClassName="aspect-video rounded-lg object-cover object-center"
                    />
                    <Button
                      asChild
                      color="inverse"
                      customIcon={<Play className="size-2 [&>path]:fill-primary" />}
                      className="pointer-events-none absolute top-1/2 left-1/2 -translate-1/2 opacity-0 shadow-sm transition-all duration-200 group-hover:opacity-100"
                    >
                      <div>Watch video</div>
                    </Button>
                  </div>
                  <p className="line-clamp-3 overflow-hidden text-left text-base font-medium text-ellipsis text-white lg:text-lg">
                    {subVideo.title}
                  </p>
                </button>
              ))}
          </RevealSection>
        </div>
      </div>
    </div>
  )
}
